/**
 * One question: what's asked, its status (changeable, with the web app's next
 * step), its length against the limit, its subheadings, and its writing plan
 * (editable, saved through mytender's collaboration service). The main action
 * is writing: Draft when the question has no answer in the document, Improve
 * when it has one; both run in the chat. Pull, Push and Use selection sit on a
 * quieter line below, and ask in place before replacing or moving text.
 * Opening the sheet as an assigned reviewer moves Awaiting review to In
 * review, as opening the section does in the web app.
 */
import { ArrowLeftIcon, ArrowUpRightIcon } from "@/design/icons";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";

import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";

import type { LimitType } from "../tender/limits";
import type { Question, SectionStatus } from "../tender/questionsApi";
import { LimitEditor } from "./LimitEditor";
import { MarkdownEditor, type MarkdownEditorHandle } from "./MarkdownEditor";
import { StatusMenu, nextStep } from "./StatusMenu";

export type QuestionSheetProps = {
  question: Question | null;
  inDocument: boolean;
  wordsInDocument: number;
  selectionWords: number;
  onClose: () => void;
  onChangeStatus: (status: SectionStatus) => void;
  onSendForReview: () => void;
  onShowInDocument: () => void;
  onSaveWritingPlan: (plan: string) => Promise<void>;
  onSaveLimit: (value: number | null, unit: LimitType) => Promise<void>;
  onDraft: (kind: "draft" | "improve") => void;
  onPull: () => void;
  onPush: () => void;
  onUseSelection: () => void;
  onOpened: () => void;
};

export const QuestionSheet: FC<QuestionSheetProps> = (props) => (
  <Sheet open={Boolean(props.question)} onOpenChange={(open) => !open && props.onClose()}>
    <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
      {props.question && <Body key={props.question.section_id} {...props} question={props.question} />}
    </SheetContent>
  </Sheet>
);

const Body: FC<QuestionSheetProps & { question: Question }> = ({
  question,
  inDocument,
  wordsInDocument,
  selectionWords,
  onClose,
  onChangeStatus,
  onSendForReview,
  onShowInDocument,
  onSaveWritingPlan,
  onSaveLimit,
  onDraft,
  onPull,
  onPush,
  onUseSelection,
  onOpened,
}) => {
  const [confirm, setConfirm] = useState<"pull" | "move" | null>(null);
  const hasText = inDocument && wordsInDocument > 0;
  const saved = question.writingplan ?? "";
  const [plan, setPlan] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // The plan is markdown, edited in place in a rich-text editor.
  const editorRef = useRef<MarkdownEditorHandle>(null);
  // The editor normalises markdown ("*" bullets become "-"), so "changed" is
  // measured against its own reading of the saved plan, not the raw text.
  const [baseline, setBaseline] = useState(saved);
  useEffect(() => {
    const normalised = editorRef.current?.markdown();
    if (normalised !== undefined) setBaseline(normalised);
  }, [saved]);
  useEffect(() => {
    onOpened();
    // once per open
    // eslint-disable-next-line
  }, []);
  const dirty = plan.trim() !== baseline.trim();
  const step = nextStep(question.status);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveWritingPlan(plan);
      setSavedAt(Date.now());
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Couldn't save the writing plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
        <button
          type="button"
          aria-label="Back to questions"
          onClick={onClose}
          className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
        >
          <ArrowLeftIcon className="size-4" />
        </button>
        <span className="text-sm font-medium text-fg-3">Question {question.section_number}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <div className="px-4 pt-4">
          <span className="text-xs font-semibold text-fg-3">
            {[question.section_number, question.weighting ? `${question.weighting} weighting` : null]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <SheetTitle className="mt-0.5 text-lg font-semibold leading-snug text-fg">
            {question.heading || "Untitled question"}
          </SheetTitle>
          <SheetDescription className="sr-only">Question details, status and writing plan</SheetDescription>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-4 pb-3 pt-2 text-xs text-fg-3">
          <StatusMenu status={question.status} onChange={onChangeStatus} />
          {step && (
            <button
              type="button"
              onClick={() => (step.kind === "open" ? onSendForReview() : onChangeStatus(step.to))}
              className="inline-flex items-center gap-0.5 font-semibold text-accent-hover hover:underline"
            >
              {step.label}
              {step.kind === "open" && <ArrowUpRightIcon className="size-3" />}
            </button>
          )}
          <span className="basis-full" />
          <span>
            <LimitEditor question={question} onSave={onSaveLimit} />
            {" · "}
            {inDocument ? `${wordsInDocument.toLocaleString()} words in document` : "Not in document"}
          </span>
          {inDocument && (
            <button
              type="button"
              onClick={onShowInDocument}
              className="font-semibold text-accent-hover hover:underline"
            >
              Show in document
            </button>
          )}
        </div>

        {question.question?.trim() && (
          <Block label="Question">
            <p className="whitespace-pre-wrap text-base text-fg-2">{question.question.trim()}</p>
          </Block>
        )}

        {question.subheadings.length > 0 && (
          <Block label="Subheadings">
            <ol className="flex flex-col gap-0.5 text-sm text-fg-2">
              {question.subheadings.map((s, i) => (
                <li key={`${s.title}-${i}`} className="flex gap-2">
                  <span className="w-3 shrink-0 text-fg-4">{String.fromCharCode(97 + i)}</span>
                  <span className="flex-1">{s.title}</span>
                  {s.word_count ? <span className="tabular-nums text-fg-4">{s.word_count} words</span> : null}
                </li>
              ))}
            </ol>
            <p className="mt-1 text-xs text-fg-4">Headings inside this answer, not separate questions.</p>
          </Block>
        )}

        <Block
          label="Writing plan"
          trailing={
            dirty ? (
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs font-semibold text-fg-3 hover:text-fg"
                  onClick={() => {
                    editorRef.current?.reset(saved);
                    setPlan(baseline);
                    setSaveError(null);
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-accent-hover hover:underline disabled:text-fg-4 disabled:no-underline"
                  onClick={() => void save()}
                  disabled={saving || !plan.trim()}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </span>
            ) : savedAt ? (
              <span className="text-xs text-fg-4">Saved</span>
            ) : null
          }
        >
          <MarkdownEditor
            ref={editorRef}
            initialMarkdown={saved}
            onChange={setPlan}
            placeholder="How the answer should be structured and which evidence to lead with. Type ## for a heading, - for a list."
          />
          <p className="mt-1 text-xs text-fg-4">Saved to mytender. io.ai follows it when drafting.</p>
          {saveError && <p className="mt-1 text-xs text-danger">{saveError}</p>}
        </Block>
      </div>

      <div className="shrink-0 border-t border-line p-3">
        {confirm ? (
          <div className="flex items-center gap-2 rounded-md bg-accent-soft px-2.5 py-2">
            <span className="min-w-0 flex-1 text-xs text-fg-2">
              {confirm === "pull"
                ? "Replace this answer with mytender's version? It arrives as tracked changes."
                : "Move the selection to the end of this answer?"}
            </span>
            <button
              type="button"
              className="rounded-sm bg-fg px-2.5 py-1 text-xs font-semibold text-fg-inverse"
              onClick={() => {
                setConfirm(null);
                if (confirm === "pull") onPull();
                else onUseSelection();
              }}
            >
              {confirm === "pull" ? "Replace" : "Move"}
            </button>
            <button
              type="button"
              className="rounded-sm px-2 py-1 text-xs font-semibold text-fg-2 hover:bg-accent-soft-2"
              onClick={() => setConfirm(null)}
            >
              {confirm === "pull" ? "Keep" : "Cancel"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onDraft(hasText ? "improve" : "draft")}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-base font-semibold text-fg-inverse transition-colors duration-fast hover:bg-accent-hover"
          >
            {hasText ? "Improve" : "Draft"}
          </button>
        )}
        <div className="mt-2 flex items-center justify-center gap-3 text-xs">
          <QuietAction onClick={() => (hasText ? setConfirm("pull") : onPull())}>Pull from mytender</QuietAction>
          {inDocument && (
            <>
              <span className="text-fg-4">·</span>
              <QuietAction onClick={onPush}>Send</QuietAction>
            </>
          )}
          {selectionWords > 0 && (
            <>
              <span className="text-fg-4">·</span>
              <QuietAction onClick={() => (inDocument ? setConfirm("move") : onUseSelection())}>
                Use selection
              </QuietAction>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const QuietAction: FC<{ onClick: () => void; children: ReactNode }> = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="font-semibold text-fg-3 hover:text-fg hover:underline">
    {children}
  </button>
);

const Block: FC<{ label: string; trailing?: ReactNode; children: ReactNode }> = ({ label, trailing, children }) => (
  <div className="px-4 pt-3">
    <div className="flex h-6 items-center">
      <span className="flex-1 text-xs font-semibold text-fg-3">{label}</span>
      {trailing}
    </div>
    <div className="mt-1">{children}</div>
  </div>
);
